import { useEffect, useState } from "react";
import { getCountries, getCountryCallingCode, isValidPhoneNumber } from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import useInputKeys from 'hooks/useInputKeys';
import { useRequestOTP } from 'hooks/auth';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import LoadingButton from 'components/Shared/LoadingButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BackspaceIcon from '@mui/icons-material/Backspace';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import 'react-phone-number-input/style.css';
import './index.scss';
import { useTheme } from "@emotion/react";

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'left',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'left',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 4,
    minWidth: 160,
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.secondary.main}`,
    color: 'white',
    marginTop: 1,
    '& .MuiMenu-list': {
      padding: 0,
      margin: 0,
    },
    '& .MuiMenuItem-root': {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: theme.palette.primary.main,
      color: '#ffffff',
      '&:hover': {
        backgroundColor: '#535353',
      },
      '&.active': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.secondary.main,
      },

      '.item-flag': {
        width: '40%',
        display: 'flex',
        alignItems: 'center',
      },
      '.item-name': {
        width: '100%',
        padding: '0 10%',
        display: 'flex',
        alignItems: 'center',
        fontSize: '1rem',
      },
      '.item-code': {
        width: '20%',
        display: 'flex',
        alignItems: 'center',
        marginRight: '8%',
        textAlign: 'right',
        fontSize: '1rem',
      },
    },
    'i': {
      backgroundColor: 'red !important'
    },
  },
}));

function PhoneForm({ phoneNumber, setPhoneNumber, setStep }) {
  const { t } = useTranslation();
  const [country, setCountry] = useState("IR");
  const [menuStatus, setMenuStatus] = useState(null);
  const [alert, setAlert] = useState('');
  const { strInput, direction, handleClearStr } = useInputKeys();
  const { mutate: sendPhoneNumber, isLoading } = useRequestOTP();
  const [wink, setWink] = useState(true);
  const open = Boolean(menuStatus);
  const theme= useTheme();

  useEffect(() => {
    const codeCountry = getContryCode(country);
    let formatedNumber = strInput.replace(/[\u0660-\u0669\u06f0-\u06f9]/g, (a) => a.charCodeAt(0) & 0xf);
    if (formatedNumber.charAt(0) === '0') {
      formatedNumber = formatedNumber.slice(1);
    }
    formatedNumber = `+${codeCountry}${formatedNumber}`;
    setPhoneNumber(formatedNumber);
    setAlert('');
  }, [strInput, country, setPhoneNumber]);

  const handleClick = (event) => {
    setMenuStatus(event.currentTarget);
  };

  const handleClose = () => {
    setMenuStatus(null);
  };

  const handleSelect = (country) => {
    setCountry(country)
    handleClose();
  };

  const handleContinue = () => {
    if (isValidPhoneNumber(phoneNumber)) {
      setAlert('');
      sendPhoneNumber(phoneNumber, {
        onSuccess: (data) => {
          if (data?.status === "OK") {
            setStep(2);
          } else if (data?.status === "ALREADY REQUESTED!") {
            setAlert('LessThan3Minutes');
          }
        },
        onError: (e) => {
          if(e.code === "ERR_BAD_REQUEST") {
            setAlert('YouAreNotCurrentlyInvited')
          }
        },
      });
    } else {
      setAlert('yourNumberIsNotValid');
    }
  };

  const countries = getCountries();
  const getContryCode = (i) => getCountryCallingCode(i);
  const getFlag = (i) => flags[i]({ title: i });

  useEffect(() => {
    setTimeout(() => {
      setWink(!wink)
    }, 500);
  }, [wink])

  return (
    <Box className={`phone-number-wrapper ${direction === 'ltr' ? "ltr-style" : "rtl-style"}`} style={{ border: `1px solid ${theme.palette.primary.main}` }}>
      <LoadingButton 
        variant="contained"
        className="login-button"
        onClick={handleContinue}
        disabled={!strInput.length}
        loading={isLoading}
        style={{ borderLeft: `1px solid ${theme.palette.primary.main}` }}
      >
        {t('continue')}
      </LoadingButton>
      <IconButton
        onClick={handleClearStr}
        disabled={!strInput.length || isLoading}
        aria-label="delete"
        size="small"
        className={`backspacebutton ${strInput.length > 0 ? "" : "hide"}`}
        style={{ color: theme.palette.primary.main }}
      >
        <BackspaceIcon />
      </IconButton>
      <InputBase
        readOnly
        className="main-input"
        sx={{
          fontFamily: 'bahnschrift',
        }}
        value={`${strInput}${wink ? "_" : ' '}`}
      />
      <Button
        disabled={isLoading}
        className="country-code-button"
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        fullWidth={true}
        style={{ borderRight: `1px solid ${theme.palette.primary.main}` }}
      >
        <div className="country-code-box">
          <ArrowDropDownIcon
            sx={{ color: 'white' }}
          />
          <div className="country">
            {flags[country]({ title: country })}
            <span>
              {country}
            </span>
          </div>
          <span className="code">
            +{getContryCode(country)}
          </span>
        </div>
      </Button>
      <StyledMenu
        id="basic-menu"
        anchorEl={menuStatus}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        PaperProps={{
          style: {
            maxHeight: '38%',
          },
        }}
      >
        {
          countries.map((item, i) => (
            <MenuItem
              key={i}
              onClick={() => handleSelect(item)}
              className={item === country ? 'active' : ''}
            >
              <div className="item-flag">
                {getFlag(item)}
              </div>
              <span className="item-name">
                {item}
              </span>
              <span className="item-code">
                +{getContryCode(item)}
              </span>
            </MenuItem>
          ))
        }
      </StyledMenu>
      <div className="input-label">
        {t('phoneNumber')}
      </div>
      {alert.length ?
        (<div className="input-alert" style={{ color: theme.palette.error.main }}>
          <InfoOutlinedIcon
            className="info-icon"
            style={{ color: theme.palette.error.main }}
          />
          <span >
            {t(alert)}
          </span>
        </div>) : null
      }
    </Box>
  );
};

export default PhoneForm;
